/**
 * SCD — Débrief pédagogique automatique (règles métier, sans IA).
 * Analyse la partie et rédige « Points forts / Points à travailler /
 * Conseils » ; affiché en fin de scénario et remonté au formateur.
 */
import { bilanAmeliorations } from './ameliorations';

export function genererDebrief(state) {
  const r = state.retail;
  const pointsForts = [];
  const aTravailler = [];
  const conseils = [];
  if (!r) return { pointsForts, aTravailler, conseils };

  const stats = r.stats || {};
  const mkt = r.marketing || {};
  const ca = stats.caCumul || 0;
  const treasury = Math.round(state.treasury || 0);

  // 1. Trésorerie
  if (treasury < 0) {
    aTravailler.push("Trésorerie négative en fin de scénario : les décaissements (achats, salaires, loyers) ont dépassé les encaissements.");
    conseils.push("Surveillez le BFR : commandez plus souvent en plus petites quantités et profitez des délais fournisseurs (30/60 j).");
  } else if (treasury >= 20000) {
    pointsForts.push(`Trésorerie solide (${treasury.toLocaleString('fr-FR')} €) : la gestion du cash est maîtrisée.`);
  }

  // 2. Marge
  if (ca > 0) {
    const margePct = Math.round(((stats.margeCumul || 0) / ca) * 100);
    if (margePct >= 45) pointsForts.push(`Taux de marge cumulé de ${margePct} % : le mix prix/positionnement est cohérent.`);
    else if (margePct < 30) {
      aTravailler.push(`Taux de marge faible (${margePct} %) : prix trop bas ou promotions trop généreuses.`);
      conseils.push("Réservez les grosses remises aux périodes commerciales (soldes, Black Friday) plutôt qu'en continu.");
    }
  }

  // 3. Ruptures
  if (ca > 0) {
    const rupturePct = Math.round(((stats.ruptureCaManqueCumul || 0) / ca) * 100);
    if (rupturePct <= 3) pointsForts.push("Très peu de ventes perdues sur rupture : l'approvisionnement suit la demande.");
    else if (rupturePct >= 10) {
      aTravailler.push(`Environ ${rupturePct} % de CA perdu sur ruptures de stock : les rayons se vident plus vite que les commandes n'arrivent.`);
      conseils.push("Anticipez les pics (week-ends, périodes commerciales) et agrandissez la réserve ou embauchez un employé de réserve.");
    }
  }

  // 4. Démarque
  if (ca > 0 && (stats.demarqueEurosCumul || 0) / ca > 0.05) {
    aTravailler.push("Démarque élevée : trop de stock dormant se déprécie en réserve.");
    conseils.push("Ajustez les quantités commandées aux ventes réelles (7 derniers jours) plutôt qu'au maximum de la réserve.");
  }

  // 5. Satisfaction & avis
  const sat = mkt.satisfaction ?? 60;
  if (sat >= 70) pointsForts.push(`Satisfaction clients de ${sat}/100 : l'expérience en magasin fidélise.`);
  else if (sat < 50) {
    aTravailler.push(`Satisfaction clients basse (${sat}/100) : ruptures, attente en caisse ou prix perçus trop élevés.`);
    conseils.push("Activez le levier Accueil & service, ajoutez une caisse ou un hôte de caisse aux heures de pointe.");
  }
  if ((mkt.noteAvis ?? 3) >= 4) pointsForts.push(`Note d'avis en ligne de ${mkt.noteAvis}★ : un vrai moteur de trafic gratuit.`);
  else if ((mkt.noteAvis ?? 3) < 2.8) aTravailler.push(`Note d'avis de ${mkt.noteAvis}★ : les avis négatifs freinent le trafic des jours suivants.`);

  // 6. Marketing
  const nbLeviers = Object.values(mkt.leviers || {}).filter(Boolean).length;
  if (nbLeviers >= 3) pointsForts.push(`${nbLeviers} leviers marketing actifs : le trafic ne repose pas que sur le passage naturel.`);
  else if (nbLeviers === 0) conseils.push("Aucun levier marketing utilisé : testez la communication locale ou la vitrine, et mesurez l'effet dans « Pourquoi ce trafic ? ».");

  // 7. Habituation promo
  if ((mkt.habituation || 0) > 0.4) {
    aTravailler.push("Promotions trop fréquentes : les clients s'y sont habitués et l'effet trafic s'est érodé.");
    conseils.push("Alternez périodes pleines et temps forts promotionnels pour recréer l'urgence d'achat.");
  }

  // 8. Équipe
  const nbEmployes = (r.equipe?.employes || []).length;
  if (nbEmployes === 0) {
    aTravailler.push("Aucun recrutement : seul en magasin, la conversion et la capacité d'encaissement plafonnent.");
  } else if (ca > 0) {
    const massePct = Math.round(((r.fiscal?.masseSalarialeCumul || 0) / ca) * 100);
    if (massePct > 35) aTravailler.push(`Masse salariale à ${massePct} % du CA : l'équipe est surdimensionnée pour l'activité actuelle.`);
    else if (nbEmployes >= 2) pointsForts.push(`Équipe de ${nbEmployes} salarié(s) bien calibrée par rapport au chiffre d'affaires.`);
  }

  // 9. E-commerce & omnicanal
  const caWeb = r.ecommerce?.stats?.caWebCumul || 0;
  if (ca > 0 && caWeb / ca > 0.1) pointsForts.push("Plus de 10 % du CA réalisé en ligne : la stratégie omnicanale porte ses fruits.");
  else if ((r.ecommerce?.palier || 0) === 0) conseils.push("Le e-commerce n'a pas été exploré : un site vitrine puis une boutique en ligne sont des relais de croissance.");

  // 10. Réseau & outil de vente
  if ((r.magasins || []).length >= 1) pointsForts.push("Ouverture d'un point de vente supplémentaire : le réseau démultiplie la demande captée.");

  // 11. SCD R3 — investissements d'amélioration du magasin.
  const bilanAmel = bilanAmeliorations(r);
  if (bilanAmel.nbTotal === 0) {
    aTravailler.push("Aucune amélioration de magasin achetée : l'outil de vente n'a pas évolué pendant le scénario.");
    conseils.push("Investissez dans l'écran « Évolution du magasin » (vitrine, réserve, caisses, click & collect…) et mesurez l'effet dans « Pourquoi ce trafic ? ».");
  } else if (bilanAmel.nbTotal >= 3) {
    pointsForts.push(`${bilanAmel.nbTotal} améliorations achetées (${bilanAmel.totalInvest.toLocaleString('fr-FR')} € investis) : l'outil de vente progresse avec l'activité.`);
    if (ca > 0 && (bilanAmel.entretienMois * 12) / Math.max(1, ca) > 0.15) {
      aTravailler.push("L'entretien des équipements pèse lourd face au CA : chaque investissement doit être rentabilisé par du trafic ou du panier.");
    }
  }

  return {
    pointsForts: pointsForts.slice(0, 5),
    aTravailler: aTravailler.slice(0, 5),
    conseils: conseils.slice(0, 4),
  };
}
